import '@/components/Faqs/Tree/tree.css';

import { Checkbox, Collapse, Group, Radio, Stack, Text, Textarea } from '@mantine/core';
import { openModal } from '@mantine/modals';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  I18N_LANG_MAP,
  queryBotDatasetsList,
  searchNodeInfoByIds,
  useBot,
  useBotStore,
  useCanAccess,
} from '@mygpt/app';
import { BindContent } from '@/components/Embeddings/BindContent';
import { FaqsTree } from '@/components/Faqs/Tree';
import { QuestionTreeTransfer } from '@/components/Widget/Embed/Form/QuestionTreeTransfer';
import { NodeItem, useWidgetFormContext } from '@/components/Widget/Embed/form-context';
import { useWidgetForm } from '@/hooks/useWidgetForm';

export const SuggestionMessage = ({ chatbotCollapseOpened }: { chatbotCollapseOpened: boolean }) => {
  const { t, i18n } = useTranslation();
  const { form, setSelectedNodesWrap, updateTransferData, widgetConfigs, setWidgetConfigs } = useWidgetFormContext();
  const { onOverrideChangeForForm } = useWidgetForm();
  const { botId } = useBot();
  const { linkedDataset, setLinkedDataset } = useBotStore();
  const canAccessWidgetCustomization = useCanAccess('enable_widget_customization');

  // 穿梭框相关状态
  const [transferNodes, setTransferNodes] = useState<NodeItem[]>([]);
  const [isLoadingTransferNodes, setIsLoadingTransferNodes] = useState(false);
  const [treeRefreshKey, setTreeRefreshKey] = useState(0); // 用于强制刷新穿梭框树数据
  const transferDatasetId = form.values.chatbot.suggestedMessages.selectedDatasetId || '';
  const transferNodeIds = form.values.chatbot.suggestedMessages.selectedNodeIds || [];
  const hasAutoSelectedRef = useRef(false);
  const lastLoadedKey = useRef<string>('');

  /** Suggest Messages of Tree **/
  const suggestTypeTextOpened = useMemo(() => {
    return form.values.chatbot.suggestedMessages.checked && form.values.chatbot.suggestedMessages.type === 'text';
  }, [form.values.chatbot.suggestedMessages.checked, form.values.chatbot.suggestedMessages.type]);

  const suggestTypeTreeOpened = useMemo(() => {
    return form.values.chatbot.suggestedMessages.checked && form.values.chatbot.suggestedMessages.type === 'tree';
  }, [form.values.chatbot.suggestedMessages.checked, form.values.chatbot.suggestedMessages.type]);

  const triggerSubmit = () => {
    // 模仿触发了 blur 时的保存效果，触发自动保存逻辑
    onOverrideChangeForForm('')({ type: 'blur', isTrusted: true });
  };

  const toSetQATree = (datasetId: string) => {
    if (!datasetId) return;
    openModal({
      withinPortal: false,
      centered: true,
      radius: 'md',
      size: '1000',
      children: <FaqsTree datasetId={datasetId} />,
      onClose: () => {
        // 关闭modal时强制刷新穿梭框的子节点数据
        setTreeRefreshKey((prev) => prev + 1);
      },
    });
  };

  const getBotDatasetList = async (id: string) => {
    try {
      const { data } = await queryBotDatasetsList({
        aiId: id,
        size: 1000,
      });

      const list = data.items;
      setLinkedDataset(list);
    } catch (error) {
      console.error('failed to getBotDatasetList, error', error);
    }
  };

  const bindDataset = () => {
    openModal({
      title: t('embeddings.dataset.add_modal.desc'),
      withinPortal: false,
      centered: true,
      radius: 'md',
      size: 'lg',
      children: (
        <BindContent
          id={botId}
          initList={false}
          onBind={() => {
            getBotDatasetList(botId);
          }}
        />
      ),
    });
  };

  const handleTransferDatasetChange = useCallback(
    (datasetId: string) => {
      const currentNodeIds = form.values.chatbot.suggestedMessages.selectedNodeIds || [];
      updateTransferData(datasetId, currentNodeIds);
      triggerSubmit();
    },
    [updateTransferData, form.values.chatbot.suggestedMessages.selectedNodeIds, triggerSubmit]
  );

  const handleTransferNodesChange = useCallback(
    (nodes: NodeItem[]) => {
      setTransferNodes(nodes);

      const nodeIds = nodes.map((node) => node.id);
      const nodeDetails = nodes.map((node) => ({
        id: node.id,
        dataset_id: node.dataset_id || transferDatasetId,
      }));

      // 更新表单数据
      form.setFieldValue('chatbot.suggestedMessages', {
        ...form.values.chatbot.suggestedMessages,
        selectedDatasetId: transferDatasetId,
        selectedNodeIds: nodeIds,
        node_list: nodeIds,
        node_details: nodeDetails,
      });

      // 同步更新相关状态
      setSelectedNodesWrap(nodes);
      setWidgetConfigs({
        ...widgetConfigs,
        chatbot: {
          ...widgetConfigs.chatbot,
          suggestedMessages: {
            ...widgetConfigs.chatbot.suggestedMessages,
            selectedDatasetId: transferDatasetId,
            selectedNodeIds: nodeIds,
            node_list: nodeIds,
            node_details: nodeDetails,
          },
        },
      });

      triggerSubmit();

      // 设置当前操作的缓存key，防止立即重复查询
      lastLoadedKey.current = `${transferDatasetId}-${nodeIds.join(',')}-${i18n.language}`;
    },
    [transferDatasetId, form, i18n.language, widgetConfigs, setSelectedNodesWrap, setWidgetConfigs, triggerSubmit]
  );

  const loadTransferNodeDetails = useCallback(
    async (datasetId: string, nodeIds: string[]) => {
      if (!datasetId || !nodeIds.length) {
        setTransferNodes([]);
        return;
      }

      const requestKey = `${datasetId}-${nodeIds.join(',')}-${i18n.language}`;

      // 防止重复请求
      if (isLoadingTransferNodes || lastLoadedKey.current === requestKey) {
        return;
      }

      setIsLoadingTransferNodes(true);
      lastLoadedKey.current = requestKey;

      try {
        const { data } = await searchNodeInfoByIds({
          ai_id: botId,
          ids: nodeIds,
          language: I18N_LANG_MAP[i18n.language],
        });

        const orderedNodes = data.filter(Boolean) as NodeItem[];

        if (orderedNodes.length === nodeIds.length) {
          // 同语言匹配成功
          setTransferNodes(orderedNodes);
          setSelectedNodesWrap(orderedNodes);
        } else {
          // 语言切换，更新为新语言数据
          const newLanguageNodes = data as NodeItem[];
          setTransferNodes(newLanguageNodes);
          setSelectedNodesWrap(newLanguageNodes);

          if (newLanguageNodes.length > 0) {
            const newNodeIds = newLanguageNodes.map((node) => node.id);
            form.setFieldValue('chatbot.suggestedMessages.selectedNodeIds', newNodeIds);
            form.setFieldValue('chatbot.suggestedMessages.node_list', newNodeIds);
            setTimeout(() => triggerSubmit(), 100);
          }
        }
      } catch (error) {
        console.error('Failed to load transfer node details:', error);
        setTransferNodes([]);
        lastLoadedKey.current = '';
      } finally {
        setIsLoadingTransferNodes(false);
      }
    },
    [botId, setSelectedNodesWrap, triggerSubmit, i18n.language]
  );

  useEffect(() => {
    botId && getBotDatasetList(botId);
  }, [botId]);

  // 初始化穿梭框数据 - 当表单数据加载完成且有保存的节点时
  useEffect(() => {
    if (!botId) return;

    // 确保表单类型为'tree'时才初始化
    if (form.values.chatbot.suggestedMessages.type === 'tree') {
      if (transferDatasetId && transferNodeIds.length > 0) {
        // 检查是否已经处理过这个请求（避免重复查询）
        const requestKey = `${transferDatasetId}-${transferNodeIds.join(',')}-${i18n.language}`;

        // 只有在完全相同的请求时才跳过，语言切换时应该重新查询
        if (lastLoadedKey.current === requestKey && !isLoadingTransferNodes) {
          return; // 跳过重复查询
        }

        // 检查是否需要重新查询
        // 1. 没有本地节点数据
        // 2. 节点数量不匹配
        // 3. 节点ID不匹配
        // 4. 语言切换时总是需要查询（缓存key已经不同了）
        const shouldRefetch =
          transferNodes.length === 0 ||
          transferNodes.length !== transferNodeIds.length ||
          !transferNodes.every((node) => transferNodeIds.includes(node.id));

        // 语言切换时，即使shouldRefetch为false，也要查询（因为缓存key已经不同）
        if (shouldRefetch || lastLoadedKey.current !== requestKey) {
          // 智能选择nodeIds来源：
          // 1. 初始化时(transferNodes为空)：使用表单的transferNodeIds
          // 2. 切换语言时(transferNodes有数据)：使用本地状态的nodeIds，避免使用过时的表单数据
          const nodeIdsToUse =
            transferNodes.length > 0
              ? transferNodes.map((node) => node.id) // 切换语言时使用本地状态
              : transferNodeIds; // 初始化时使用表单数据

          loadTransferNodeDetails(transferDatasetId, nodeIdsToUse);
        }
      } else if (transferNodeIds.length === 0 && transferNodes.length > 0) {
        // 只有在确实需要清空时才清空
        setTransferNodes([]);
        setSelectedNodesWrap([]);
      }
    }
  }, [transferDatasetId, transferNodeIds.join(','), botId, form.values.chatbot.suggestedMessages.type, i18n.language]);

  // 保持节点顺序与表单一致
  useEffect(() => {
    if (transferNodeIds.length > 0 && transferNodes.length > 0) {
      const currentOrder = transferNodes.map((n) => n.id).join(',');
      const expectedOrder = transferNodeIds.join(',');

      if (currentOrder !== expectedOrder && transferNodes.length === transferNodeIds.length) {
        const orderedTransferNodes = transferNodeIds
          .map((nodeId) => transferNodes.find((node) => node.id === nodeId))
          .filter(Boolean) as NodeItem[];

        if (orderedTransferNodes.length === transferNodeIds.length) {
          setTransferNodes(orderedTransferNodes);
          setSelectedNodesWrap(orderedTransferNodes);
        }
      }
    }
  }, [transferNodeIds]);

  // 自动选择首个知识库
  useEffect(() => {
    if (
      !transferDatasetId &&
      linkedDataset.length > 0 &&
      form.values.chatbot.suggestedMessages.type === 'tree' &&
      !hasAutoSelectedRef.current
    ) {
      hasAutoSelectedRef.current = true;
      const firstDatasetId = linkedDataset[0].id;
      form.setFieldValue('chatbot.suggestedMessages.selectedDatasetId', firstDatasetId);
    }
  }, [linkedDataset, transferDatasetId, form.values.chatbot.suggestedMessages.type, form]);

  // 重置自动选择标记
  useEffect(() => {
    if (form.values.chatbot.suggestedMessages.type !== 'tree' || transferDatasetId) {
      hasAutoSelectedRef.current = false;
    }
  }, [form.values.chatbot.suggestedMessages.type, transferDatasetId]);

  return (
    <Stack>
      <Checkbox
        disabled={!canAccessWidgetCustomization}
        label={<Text>{t('widget.setting.chatbot.suggested_messages_label')}</Text>}
        description={t('widget.setting.chatbot.suggested_messages_desc')}
        checked={form.values.chatbot.suggestedMessages.checked}
        {...form.getInputProps('chatbot.suggestedMessages.checked')}
        onChange={onOverrideChangeForForm('chatbot.suggestedMessages.checked')}
      />

      {/* 自定义引导文案配置 */}
      <Stack gap="xs" className="border rounded-lg p-3">
        <Checkbox
          disabled={!canAccessWidgetCustomization || !form.values.chatbot.suggestedMessages.checked}
          label={<Text size="sm">{t('widget.setting.chatbot.suggested_greeting_message_custom_label')}</Text>}
          checked={form.values.chatbot.suggestedMessages.greetingMessage?.checked || false}
          {...form.getInputProps('chatbot.suggestedMessages.greetingMessage.checked')}
          onChange={onOverrideChangeForForm('chatbot.suggestedMessages.greetingMessage.checked')}
        />
        <Text size="xs" c="gray.6">
          {t('widget.setting.chatbot.suggested_greeting_message_custom_desc')}
        </Text>
        <Collapse in={form.values.chatbot.suggestedMessages.greetingMessage?.checked || false}>
          <Textarea
            disabled={!canAccessWidgetCustomization || !form.values.chatbot.suggestedMessages.checked}
            autosize
            minRows={2}
            maxRows={4}
            placeholder={t('widget.setting.chatbot.suggested_greeting_message_custom_placeholder')}
            value={form.values.chatbot.suggestedMessages.greetingMessage?.value || ''}
            {...form.getInputProps('chatbot.suggestedMessages.greetingMessage.value')}
            onBlur={onOverrideChangeForForm('chatbot.suggestedMessages.greetingMessage.value')}
          />
        </Collapse>
      </Stack>
      <Radio.Group
        {...form.getInputProps('chatbot.suggestedMessages.type')}
        onChange={onOverrideChangeForForm('chatbot.suggestedMessages.type')}
      >
        <section className="border rounded-lg p-3 mb-2">
          <Radio
            size="xs"
            label={t('widget.setting.chatbot.suggested_text')}
            value="text"
            disabled={!form.values.chatbot.suggestedMessages.checked}
          />
          <Collapse in={chatbotCollapseOpened && suggestTypeTextOpened}>
            <Textarea
              className="mt-4"
              disabled={!form.values.chatbot.suggestedMessages.checked}
              autosize
              minRows={3}
              {...form.getInputProps('chatbot.suggestedMessages.value')}
              onBlur={onOverrideChangeForForm('chatbot.suggestedMessages.value')}
            />
          </Collapse>
        </section>
        <section className="border rounded-lg p-3">
          <Radio
            size="xs"
            label={t('widget.setting.chatbot.suggested_tree')}
            value="tree"
            disabled={!form.values.chatbot.suggestedMessages.checked}
          />
          <Collapse in={chatbotCollapseOpened && suggestTypeTreeOpened}>
            {linkedDataset.length > 0 ? (
              <div className="mt-4">
                <QuestionTreeTransfer
                  key={`${transferDatasetId}-${treeRefreshKey}`} // 使用refreshKey强制重新渲染
                  datasets={linkedDataset}
                  selectedDatasetId={transferDatasetId}
                  selectedNodes={transferNodes}
                  onDatasetChange={handleTransferDatasetChange}
                  onNodesChange={handleTransferNodesChange}
                  disabled={!form.values.chatbot.suggestedMessages.checked}
                  onTreeSetup={toSetQATree}
                />
              </div>
            ) : (
              <Group gap={5} className="mt-4">
                <Text c="gray.6" size="xs">
                  {t('widget.setting.chatbot.no_dataset_tip')}
                </Text>
                <Text c="violet.8" size="xs" className="cursor-pointer" onClick={bindDataset}>
                  {t('widget.setting.chatbot.no_dataset_set')}
                </Text>
              </Group>
            )}
          </Collapse>
        </section>
      </Radio.Group>
    </Stack>
  );
};
